// Gerador de códigos únicos para imóveis
// Formato: XXNNNNN onde XX são letras do tipo e NNNNN são 5 dígitos

interface PropertyTypeCodeMap {
  [key: string]: string;
}

// Mapeamento específico para evitar conflitos entre tipos similares
const PROPERTY_TYPE_CODES: PropertyTypeCodeMap = {
  // Residencial
  'Residential / Apartment': 'AP',
  'Residential / Home': 'CA', // Casa
  'Residential / Condominium': 'CC', // Casa de Condomínio
  'Residential / Penthouse': 'CO',
  'Residential / Flat': 'FL',
  'Residential / Studio': 'ST',
  'Residential / Loft': 'LO',
  'Residential / Duplex': 'DU',
  'Residential / Triplex': 'TR',
  'Residential / Quadruplex': 'QU',
  'Residential / Village House': 'CV', // Casa de Vila
  'Residential / Farm': 'CH', // Chácara
  'Residential / Farm Ranch': 'FA', // Fazenda
  'Residential / Sobrado': 'SO',
  'Residential / Land Lot': 'TE', // Terreno

  // Comercial
  'Commercial / Building': 'PR', // Prédio
  'Commercial / Office': 'SA', // Sala
  'Commercial / Shop': 'LJ', // Loja
  'Commercial / Warehouse': 'GA', // Galpão
  'Commercial / Industrial Shed': 'BA', // Barracão
  'Commercial / Business Point': 'PC', // Ponto Comercial

  // Outros (fallback)
  'Other': 'OU'
};

// Suporte a nomes em Português e variações comuns
const PROPERTY_TYPE_CODES_PT: PropertyTypeCodeMap = {
  'Apartamento': 'AP',
  'Casa': 'CA',
  'Casa de Condomínio': 'CC',
  'Cobertura': 'CO',
  'Flat': 'FL',
  'Studio': 'ST',
  'Stúdio': 'ST',
  'Loft': 'LO',
  'Duplex': 'DU',
  'Triplex': 'TR',
  'Casa de Vila': 'CV',
  'Chácara': 'CH',
  'Fazenda': 'FA',
  'Sítio': 'FA',
  'Terreno': 'TE',
  'Prédio': 'PR',
  'Edifício': 'PR',
  'Sala': 'SA',
  'Loja': 'LJ',
  'Galpão': 'GA',
  'Barracão': 'BA',
  'Ponto Comercial': 'PC',
  'Comercial': 'PC',
  'Outro': 'OU'
};

function resolvePrefix(typeRaw: string): string {
  const type = (typeRaw || '').trim();
  if (!type) return 'GE';

  // 1) Match exato em EN/PT
  if (PROPERTY_TYPE_CODES[type]) return PROPERTY_TYPE_CODES[type];
  if (PROPERTY_TYPE_CODES_PT[type]) return PROPERTY_TYPE_CODES_PT[type];

  // 2) Heurística por palavras-chave (case-insensitive)
  const t = type.toLowerCase();
  if (t.includes('apart')) return 'AP';
  if (t.includes('condom')) return 'CC';
  if (t.includes('cobert')) return 'CO';
  if (t.includes('flat')) return 'FL';
  if (t.includes('studio') || t.includes('stúdio')) return 'ST';
  if (t.includes('loft')) return 'LO';
  if (t.includes('duplex')) return 'DU';
  if (t.includes('triplex')) return 'TR';
  if (t.includes('vila')) return 'CV';
  if (t.includes('chácara')) return 'CH';
  if (t.includes('fazend') || t.includes('sítio') || t.includes('sitio')) return 'FA';
  if (t.includes('sobrado')) return 'SO';
  if (t.includes('terreno') || t.includes('lote')) return 'TE';
  if (t.includes('prédio') || t.includes('predio') || t.includes('edif')) return 'PR';
  if (t.includes('sala')) return 'SA';
  if (t.includes('loja')) return 'LJ';
  if (t.includes('galp')) return 'GA';
  if (t.includes('barrac')) return 'BA';
  if (t.includes('ponto')) return 'PC';
  if (t.includes('casa')) return 'CA';

  return 'GE';
}

// Storage para códigos já gerados (simulando banco de dados)
const USED_CODES = new Set<string>();

/**
 * Gera um código único para um imóvel
 * @param propertyType - Tipo do imóvel
 * @returns Código único no formato XXNNNNN
 */
export function generatePropertyCode(propertyType: string): string {
  // Obter o prefixo de 2 letras com suporte a EN/PT e heurísticas
  const prefix = resolvePrefix(propertyType);
  
  let attempts = 0;
  const maxAttempts = 10000; // Evitar loop infinito
  
  while (attempts < maxAttempts) {
    // Gerar número aleatório de 5 dígitos (00001 a 99999)
    const number = Math.floor(Math.random() * 99999) + 1;
    const paddedNumber = number.toString().padStart(5, '0');
    
    const code = `${prefix}${paddedNumber}`;
    
    // Verificar se o código já foi usado
    if (!USED_CODES.has(code)) {
      USED_CODES.add(code);
      return code;
    }
    
    attempts++;
  }
  
  // Se não conseguir gerar um código único, usar timestamp como fallback
  const timestamp = Date.now().toString().slice(-5);
  const fallbackCode = `${prefix}${timestamp}`;
  USED_CODES.add(fallbackCode);
  
  console.warn(`Gerador de código atingiu limite de tentativas. Usando código com timestamp: ${fallbackCode}`);
  return fallbackCode;
}

/**
 * Verifica se um código já está em uso
 * @param code - Código para verificar
 * @returns true se o código está em uso
 */
export function isCodeInUse(code: string): boolean {
  return USED_CODES.has(code);
}

/**
 * Adiciona um código existente ao conjunto de códigos usados
 * (útil para carregar códigos já existentes do banco de dados)
 * @param code - Código existente
 */
export function addExistingCode(code: string): void {
  USED_CODES.add(code);
}

/**
 * Obter o prefixo de um tipo de imóvel
 * @param propertyType - Tipo do imóvel
 * @returns Prefixo de 2 letras
 */
export function getPropertyTypePrefix(propertyType: string): string {
  return resolvePrefix(propertyType);
}

/**
 * Validar formato de código
 * @param code - Código para validar
 * @returns true se o formato está correto
 */
export function validateCodeFormat(code: string): boolean {
  // Formato: 2 letras + 5 dígitos = 7 caracteres
  const codeRegex = /^[A-Z]{2}\d{5}$/;
  return codeRegex.test(code);
}

/**
 * Inicializar o gerador com códigos existentes
 * @param existingCodes - Array de códigos já existentes no sistema
 */
export function initializeWithExistingCodes(existingCodes: string[]): void {
  existingCodes.forEach(code => {
    if (validateCodeFormat(code)) {
      USED_CODES.add(code);
    }
  });
  
  console.log(`Gerador inicializado com ${USED_CODES.size} códigos existentes`);
}